import { createContext, useContext, useState, useEffect } from 'react';

/**
 * SettingsContext
 * Global app settings management
 * Integration point: Sync with Firebase Firestore settings document
 */

const SettingsContext = createContext(null);

const DEFAULT_SETTINGS = {
  // Driver settings
  driver_radius_km: 10,
  commission_type: 'percentage', // 'fixed' | 'percentage'
  commission_value: 15,

  // App toggles
  night_mode: false,
  maintenance_mode: false,
  new_registrations_enabled: true,

  // Canned messages
  canned_messages: {
    order_assigned: 'Your order has been assigned to a driver.',
    order_completed: 'Your order has been delivered successfully.',
    order_cancelled: 'Your order has been cancelled.',
    driver_on_way: 'Your driver is on the way.',
  },

  // Notification settings
  notifications_enabled: true,
  email_notifications: true,

  // Splash image
  splash_image_url: '/assets/splash-default.png',

  // Delete request settings
  delete_request_review_days: 7,
  delete_request_auto_approve: false,

  // Last updated
  updated_at: new Date(),
  updated_by: null,
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    // Integration point: Fetch settings from Firestore
    // const unsubscribe = firebase.firestore()
    //   .collection('settings')
    //   .doc('global')
    //   .onSnapshot((doc) => {
    //     if (doc.exists) {
    //       setSettings({ ...DEFAULT_SETTINGS, ...doc.data() });
    //     }
    //     setLoading(false);
    //   });
    // return unsubscribe;

    // Mock data loading
    setTimeout(() => {
      setLoading(false);
    }, 300);
  }, []);

  const updateSettings = async (newSettings) => {
    // Integration point: Update Firestore settings document
    // await firebase.firestore()
    //   .collection('settings')
    //   .doc('global')
    //   .set({
    //     ...newSettings,
    //     updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    //     updated_by: currentUserId,
    //   }, { merge: true });

    const updatedSettings = {
      ...settings,
      ...newSettings,
      updated_at: new Date(),
    };

    setSettings(updatedSettings);
    setLastSaved(new Date());
    setHasUnsavedChanges(false);

    console.log('Settings updated:', updatedSettings);
    return Promise.resolve(updatedSettings);
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    setHasUnsavedChanges(false);
  };

  const getSetting = (key) => {
    return settings[key];
  };

  const setSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const value = {
    settings,
    loading,
    lastSaved,
    hasUnsavedChanges,
    updateSettings,
    resetSettings,
    getSetting,
    setSetting,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
}

export default SettingsContext;
